import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { supabase } from '../lib/supabase';
import { toast } from 'react-hot-toast';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { EyeSlashIcon } from '@heroicons/react/24/outline';
import {
  NewspaperIcon,
  CalendarIcon,
  UserGroupIcon,
  CameraIcon,
  MicrophoneIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  PlusIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

// Define the form schema
const pressRegistrationSchema = z.object({
  firstName: z.string().min(2, { message: "First name must be at least 2 characters" }),
  lastName: z.string().min(2, { message: "Last name must be at least 2 characters" }),
  email: z.string().email({ message: "Invalid email address" }),
  phone: z.string().min(10, { message: "Phone number must be at least 10 characters" }),
  mediaOutlet: z.string().min(2, { message: "Media outlet name is required" }),
  position: z.string().min(2, { message: "Position is required" }),
  mediaType: z.array(z.string()).min(1, { message: "At least one media type is required" }),
  otherMediaType: z.string().optional(),
  teamSize: z.string().min(1, { message: "Team size is required" }),
  equipmentDetails: z.string().optional(),
  coverageMode: z.string().min(1, { message: "Please select a coverage mode" }),
  coveragePlans: z.string().min(10, { message: "Please provide details about your coverage plans" }),
  arrivalDate: z.string().min(1, { message: "Arrival date is required" }),
  specialRequests: z.string().optional(),
  tournamentId: z.string().optional()
});

type PressRegistrationFormData = z.infer<typeof pressRegistrationSchema>;

export default function PressRegistration() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [registrationNumber, setRegistrationNumber] = useState('');
  const [guidelines, setGuidelines] = useState('');
  const [loading, setLoading] = useState(true);
  const [isPublished, setIsPublished] = useState(true);
  const [tournaments, setTournaments] = useState<{id: string, name: string}[]>([]);
  const [teamMembers, setTeamMembers] = useState<{ firstName: string; lastName: string }[]>([{ firstName: '', lastName: '' }]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
    setValue
  } = useForm<PressRegistrationFormData>({
    resolver: zodResolver(pressRegistrationSchema)
  });

  const selectedMediaTypes = watch('mediaType') as string[] | undefined;
  const selectedTeamSize = watch('teamSize');
  const isMultiPerson = selectedTeamSize && selectedTeamSize !== '1';

  const updateTeamMember = (index: number, field: 'firstName' | 'lastName', value: string) => {
    setTeamMembers(prev => prev.map((m, i) => i === index ? { ...m, [field]: value } : m));
  };

  const addTeamMember = () => setTeamMembers(prev => [...prev, { firstName: '', lastName: '' }]);
  const removeTeamMember = (index: number) => setTeamMembers(prev => prev.filter((_, i) => i !== index));

  const toggleMediaType = (value: string) => {
    const current = selectedMediaTypes ?? [];
    const updated = current.includes(value)
      ? current.filter(v => v !== value)
      : [...current, value];
    setValue('mediaType', updated, { shouldValidate: true });
  };

  useEffect(() => {
    fetchGuidelines();
    checkPressPublishStatus();
    fetchTournaments();
  }, []);

  const checkPressPublishStatus = async () => {
    try {
      const { data, error } = await supabase
        .from('system_settings')
        .select('value')
        .eq('key', 'press_published')
        .maybeSingle();
      
      if (error && !error.message.includes('No rows found')) {
        console.error('Error checking press publish status:', error);
        return;
      }
      
      // If the setting exists, use its value, otherwise default to true
      setIsPublished(data?.value === true || data?.value === 'true');
    } catch (err) {
      console.error('Error checking press publish status:', err);
    }
  };

  const fetchGuidelines = async () => {
    try {
      setLoading(true);
      
      // Fetch media guidelines from content_items
      const { data, error } = await supabase
        .from('content_items')
        .select('content')
        .eq('title', 'Media Guidelines')
        .eq('status', 'Published')
        .maybeSingle();
      
      if (error) {
        console.error('Error fetching guidelines:', error);
        return;
      }
      
      if (data && data.content) {
        // Parse content if it's a string
        let content = data.content;
        if (typeof content === 'string') {
          try {
            // Try to parse as JSON first
            content = JSON.parse(content);
          } catch (e) {
            // If not valid JSON, use as is
            console.log('Content is not valid JSON, using as is');
          }
        }
        
        if (typeof content === 'object' && content !== null) {
          setGuidelines(content.markdown || content.text || '');
        } else {
          setGuidelines(content || '');
        }
      }
    } catch (error) {
      console.error('Error fetching guidelines:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTournaments = async () => {
    try {
      // Fetch upcoming tournaments
      const { data, error } = await supabase
        .from('tournaments')
        .select('id, name')
        .eq('status', 'upcoming')
        .order('start_date', { ascending: true });
      
      if (error) throw error;
      
      if (data && data.length > 0) {
        setTournaments(data);
        // Set the first tournament as default
        setValue('tournamentId', data[0].id);
      }
    } catch (error) {
      console.error('Error fetching tournaments:', error);
    }
  };

  const onSubmit = async (data: PressRegistrationFormData) => {
    try {
      setIsSubmitting(true);
      
      // Generate a registration number
      const regNumber = `PRESS-${new Date().getFullYear()}-${Math.random().toString(36).substring(2, 8).toUpperCase()}`;
      setRegistrationNumber(regNumber);
      
      // Common fields shared across all rows in this registration group
      const common = {
        email: data.email,
        phone: data.phone,
        media_outlet: data.mediaOutlet,
        position: data.position,
        media_type: data.mediaType,
        other_media_type: data.mediaType.includes('Other') ? data.otherMediaType : null,
        team_size: data.teamSize,
        equipment_details: data.equipmentDetails || null,
        coverage_mode: data.coverageMode,
        coverage_plans: data.coveragePlans,
        arrival_date: data.arrivalDate,
        special_requests: data.specialRequests || null,
        registration_number: regNumber,
        status: 'pending',
        tournament_id: data.tournamentId || null,
      };

      // Build rows: primary registrant first, then one row per named team member
      const validMembers = isMultiPerson ? teamMembers.filter(m => m.firstName.trim() || m.lastName.trim()) : [];
      const rows = [
        { ...common, first_name: data.firstName, last_name: data.lastName, is_primary: true },
        ...validMembers.map(m => ({
          ...common,
          first_name: m.firstName.trim(),
          last_name: m.lastName.trim(),
          is_primary: false,
        })),
      ];

      // Submit to Supabase
      const { error } = await supabase
        .from('press_registrations')
        .insert(rows);

      if (error) throw error;

      // Show confirmation
      setShowConfirmation(true);
      setTeamMembers([{ firstName: '', lastName: '' }]);
      reset();
      
    } catch (error) {
      console.error('Error submitting press registration:', error);
      toast.error('Failed to submit registration. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="pt-20">
      {/* Unpublished Notice */}
      {!isPublished && (
        <div className="fixed top-24 inset-x-0 z-50 bg-yellow-100 border-b border-yellow-200 py-2">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-center gap-2 text-yellow-800">
              <EyeSlashIcon className="w-5 h-5" />
              <p className="text-sm font-medium">
                This page is currently unpublished and only visible to administrators
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="relative bg-golf-gray text-white py-24">
        <div className="absolute inset-0 bg-[url('https://wjwcuakqeesxnbjznajh.supabase.co/storage/v1/object/public/images/webcontents/0B6A9777.jpg')] bg-cover bg-center opacity-20"></div>
        <div className="container mx-auto px-4 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto text-center"
          >
            <h1 className="text-6xl font-display font-bold mb-6">
              Press Registration
            </h1>
            <p className="text-xl text-golf-sand mb-8">
              Register for media coverage of the Ibom Open
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-3 gap-8">
              {/* Left Column - Registration Form */}
              <div className="lg:col-span-2">
                {showConfirmation ? (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="bg-white rounded-2xl shadow-lg p-8 text-center"
                  >
                    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <CheckCircleIcon className="w-10 h-10 text-green-600" />
                    </div>
                    <h2 className="text-3xl font-display font-bold text-golf-gray mb-4">
                      Registration Successful
                    </h2>
                    <p className="text-gray-600 mb-4">
                      Thank you for registering as press for the Ibom Open. Your registration has been received and is being processed.
                    </p>
                    <p className="text-gray-600 mb-6">
                      Your registration number is: <span className="font-bold">{registrationNumber}</span>
                    </p>
                    <p className="text-gray-600 mb-8">
                      Please keep this number for your records. We will contact you with further instructions regarding your press accreditation.
                    </p>
                    <button
                      onClick={() => setShowConfirmation(false)}
                      className="modern-button px-8 py-3 text-white text-lg font-medium"
                    >
                      Register Another
                    </button>
                  </motion.div>
                ) : (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="bg-white rounded-2xl shadow-lg p-8"
                  >
                    <h2 className="text-3xl font-display font-bold text-golf-gray mb-6">
                      Media Registration Form
                    </h2>
                    <p className="text-gray-600 mb-8">
                      Complete this form to request press accreditation for the Ibom Open. All fields marked with an asterisk (*) are required.
                    </p>

                    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                      {/* Personal Information */}
                      <div>
                        <h3 className="text-xl font-bold text-golf-gray mb-4">Personal Information</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                            <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                              First Name <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              id="firstName"
                              {...register("firstName")}
                              className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
                            />
                            {errors.firstName && (
                              <p className="mt-1 text-sm text-red-600">{errors.firstName.message}</p>
                            )}
                          </div>
                          <div>
                            <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                              Last Name <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              id="lastName"
                              {...register("lastName")}
                              className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
                            />
                            {errors.lastName && (
                              <p className="mt-1 text-sm text-red-600">{errors.lastName.message}</p>
                            )}
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                          <div>
                            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                              Email Address <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="email"
                              id="email"
                              {...register("email")}
                              className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
                            />
                            {errors.email && (
                              <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                            )}
                          </div>
                          <div>
                            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                              Phone Number <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="tel"
                              id="phone"
                              {...register("phone")}
                              className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
                              placeholder="+234 800 000 0000"
                            />
                            {errors.phone && (
                              <p className="mt-1 text-sm text-red-600">{errors.phone.message}</p>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Media Information */}
                      <div className="pt-4 border-t border-gray-200">
                        <h3 className="text-xl font-bold text-golf-gray mb-4">Media Information</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                            <label htmlFor="mediaOutlet" className="block text-sm font-medium text-gray-700 mb-1">
                              Media Outlet <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              id="mediaOutlet"
                              {...register("mediaOutlet")}
                              className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
                              placeholder="e.g., Golf Nigeria, Sports Africa"
                            />
                            {errors.mediaOutlet && (
                              <p className="mt-1 text-sm text-red-600">{errors.mediaOutlet.message}</p>
                            )}
                          </div>
                          <div>
                            <label htmlFor="position" className="block text-sm font-medium text-gray-700 mb-1">
                              Position <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              id="position"
                              {...register("position")}
                              className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
                              placeholder="e.g., Journalist, Photographer, Editor"
                            />
                            {errors.position && (
                              <p className="mt-1 text-sm text-red-600">{errors.position.message}</p>
                            )}
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Media Type <span className="text-red-500">*</span>
                            </label>
                            <div className="flex flex-wrap gap-2">
                              {['Print', 'Digital', 'TV', 'Radio', 'Photography', 'Other'].map(type => (
                                <button
                                  key={type}
                                  type="button"
                                  onClick={() => toggleMediaType(type)}
                                  className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                                    (selectedMediaTypes ?? []).includes(type)
                                      ? 'bg-golf-orange text-white border-golf-orange'
                                      : 'bg-white text-gray-600 border-gray-300 hover:border-golf-orange'
                                  }`}
                                >
                                  {type}
                                </button>
                              ))}
                            </div>
                            {errors.mediaType && (
                              <p className="mt-1 text-sm text-red-600">{errors.mediaType.message}</p>
                            )}
                          </div>
                          {(selectedMediaTypes ?? []).includes('Other') && (
                            <div>
                              <label htmlFor="otherMediaType" className="block text-sm font-medium text-gray-700 mb-1">
                                Please Specify <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="text"
                                id="otherMediaType"
                                {...register("otherMediaType")}
                                className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
                              />
                              {errors.otherMediaType && (
                                <p className="mt-1 text-sm text-red-600">{errors.otherMediaType.message}</p>
                              )}
                            </div>
                          )}
                          <div>
                            <label htmlFor="teamSize" className="block text-sm font-medium text-gray-700 mb-1">
                              Team Size <span className="text-red-500">*</span>
                            </label>
                            <select
                              id="teamSize"
                              {...register("teamSize")}
                              className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
                            >
                              <option value="">Select team size</option>
                              <option value="1">Just me (1 person)</option>
                              <option value="2-3">Small team (2-3 people)</option>
                              <option value="4-6">Medium team (4-6 people)</option>
                              <option value="7+">Large team (7+ people)</option>
                            </select>
                            {errors.teamSize && (
                              <p className="mt-1 text-sm text-red-600">{errors.teamSize.message}</p>
                            )}
                          </div>
                        </div>
                        {isMultiPerson && (
                          <div className="mt-6">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Team Member Names
                            </label>
                            <div className="space-y-3">
                              {teamMembers.map((member, index) => (
                                <div key={index} className="flex items-center gap-2">
                                  <span className="text-xs text-gray-400 w-5 text-right shrink-0">{index + 1}.</span>
                                  <input
                                    type="text"
                                    value={member.firstName}
                                    onChange={e => updateTeamMember(index, 'firstName', e.target.value)}
                                    placeholder="First name"
                                    className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange text-sm"
                                  />
                                  <input
                                    type="text"
                                    value={member.lastName}
                                    onChange={e => updateTeamMember(index, 'lastName', e.target.value)}
                                    placeholder="Last name"
                                    className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange text-sm"
                                  />
                                  {teamMembers.length > 1 && (
                                    <button
                                      type="button"
                                      onClick={() => removeTeamMember(index)}
                                      className="shrink-0 text-gray-400 hover:text-red-500 transition-colors"
                                    >
                                      <XMarkIcon className="w-4 h-4" />
                                    </button>
                                  )}
                                </div>
                              ))}
                            </div>
                            <button
                              type="button"
                              onClick={addTeamMember}
                              className="mt-2 flex items-center gap-1 text-sm text-golf-orange hover:text-golf-orange/80 transition-colors"
                            >
                              <PlusIcon className="w-4 h-4" />
                              Add another member
                            </button>
                          </div>
                        )}
                        <div className="mt-6">
                          <label htmlFor="equipmentDetails" className="block text-sm font-medium text-gray-700 mb-1">
                            Equipment Details
                          </label>
                          <textarea
                            id="equipmentDetails"
                            {...register("equipmentDetails")}
                            rows={3}
                            className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
                            placeholder="Please list any special equipment you'll be bringing (cameras, tripods, recording devices, etc.)"
                          />
                          {errors.equipmentDetails && (
                            <p className="mt-1 text-sm text-red-600">{errors.equipmentDetails.message}</p>
                          )}
                        </div>
                      </div>

                      {/* Coverage Details */}
                      <div className="pt-4 border-t border-gray-200">
                        <h3 className="text-xl font-bold text-golf-gray mb-4">Coverage Details</h3>

                        <div className="mb-6">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Coverage Mode <span className="text-red-500">*</span>
                          </label>
                          <p className="text-xs text-gray-500 mb-3">How will you be covering the event?</p>
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            {[
                              { value: 'in-person', label: 'In-Person', description: 'On-site at the event' },
                              { value: 'remote', label: 'Remote / Syndicate', description: 'Via syndicate feeds' },
                              { value: 'both', label: 'Both', description: 'In-person and syndicate' },
                            ].map(option => (
                              <label
                                key={option.value}
                                className={`relative flex flex-col items-center p-4 border-2 rounded-xl cursor-pointer transition-all ${
                                  watch('coverageMode') === option.value
                                    ? 'border-golf-orange bg-golf-orange/5 ring-1 ring-golf-orange'
                                    : 'border-gray-200 hover:border-gray-300'
                                }`}
                              >
                                <input
                                  type="radio"
                                  value={option.value}
                                  {...register("coverageMode")}
                                  className="sr-only"
                                />
                                <span className="text-sm font-semibold text-gray-900">{option.label}</span>
                                <span className="text-[11px] text-gray-500 mt-0.5 text-center">{option.description}</span>
                              </label>
                            ))}
                          </div>
                          {errors.coverageMode && (
                            <p className="mt-1 text-sm text-red-600">{errors.coverageMode.message}</p>
                          )}
                        </div>

                        <div>
                          <label htmlFor="coveragePlans" className="block text-sm font-medium text-gray-700 mb-1">
                            Coverage Plans <span className="text-red-500">*</span>
                          </label>
                          <textarea
                            id="coveragePlans"
                            {...register("coveragePlans")}
                            rows={4}
                            className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
                            placeholder="Please describe your coverage plans for the tournament (e.g., daily reports, player interviews, feature stories)"
                          />
                          {errors.coveragePlans && (
                            <p className="mt-1 text-sm text-red-600">{errors.coveragePlans.message}</p>
                          )}
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                          <div>
                            <label htmlFor="arrivalDate" className="block text-sm font-medium text-gray-700 mb-1">
                              Planned Arrival Date <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="date"
                              id="arrivalDate"
                              {...register("arrivalDate")}
                              className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
                            />
                            {errors.arrivalDate && (
                              <p className="mt-1 text-sm text-red-600">{errors.arrivalDate.message}</p>
                            )}
                          </div>
                          <div>
                            <label htmlFor="tournamentId" className="block text-sm font-medium text-gray-700 mb-1">
                              Tournament
                            </label>
                            <select
                              id="tournamentId"
                              {...register("tournamentId")}
                              className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
                            >
                              {tournaments.map(tournament => (
                                <option key={tournament.id} value={tournament.id}>
                                  {tournament.name}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div className="mt-6">
                          <label htmlFor="specialRequests" className="block text-sm font-medium text-gray-700 mb-1">
                            Special Requests
                          </label>
                          <textarea
                            id="specialRequests"
                            {...register("specialRequests")}
                            rows={3}
                            className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
                            placeholder="Any special requests or accommodations needed"
                          />
                          {errors.specialRequests && (
                            <p className="mt-1 text-sm text-red-600">{errors.specialRequests.message}</p>
                          )}
                        </div>
                      </div>

                      <div className="pt-6 border-t border-gray-200">
                        <button
                          type="submit"
                          disabled={isSubmitting}
                          className="modern-button w-full py-3 text-white font-medium text-lg"
                        >
                          {isSubmitting ? 'Submitting...' : 'Submit Registration'}
                        </button>
                        <p className="text-sm text-gray-500 mt-4 text-center">
                          By submitting this form, you agree to abide by the media guidelines and code of conduct for the Ibom Open 2025.
                        </p>
                      </div>
                    </form>
                  </motion.div>
                )}
              </div>

              {/* Right Column - Guidelines and Info */}
              <div>
                {/* Media Guidelines */}
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5 }}
                  className="bg-white rounded-2xl shadow-lg p-6 mb-8"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-golf-orange/10 rounded-lg">
                      <DocumentTextIcon className="w-6 h-6 text-golf-orange" />
                    </div>
                    <h3 className="text-xl font-bold text-golf-gray">Media Guidelines</h3>
                  </div>
                  
                  {loading ? (
                    <div className="animate-pulse space-y-4">
                      <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                      <div className="h-4 bg-gray-200 rounded"></div>
                      <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                    </div>
                  ) : (
                    <div className="prose prose-sm max-w-none">
                      <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(marked(guidelines) as string) }} />
                    </div>
                  )}
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}